import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, FileText, Sparkles, CheckCircle, Clock, User, ChevronRight, TrendingUp, Wallet, Calendar, Star, KeyRound, Heart, AlertTriangle, Ban, Check } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Tabs } from '../../components/ui/Tabs';
import { useScreenInit } from '../../useScreenInit.js';
import { getApartmentById, getApartmentSpecs, TYPE_STYLE, scoreColor } from './appartamentiData';
type DetailTab = 'overview' | 'specifiche' | 'interventi' | 'contratto';
function StatCard({
  icon,
  label,
  value,
  sub,
  accent = 'navy'
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: 'navy' | 'coral' | 'teal-deep' | 'emerald';
}) {
  return <div className="bg-white border border-border rounded-xl p-4" data-id="element-1496">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 bg-coral/10 text-coral" data-id="element-1497">
        {icon}
      </div>
      <div className="text-lg font-semibold text-navy leading-tight" data-id="element-1498">
        {value}
      </div>
      <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1499">{label}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1" data-id="element-1500">{sub}</div>}
    </div>;
}
export function CmAppartamentoDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const apartment = getApartmentById(id);
  const [tab, setTab] = useState<DetailTab>('overview');
  if (!apartment) {
    return <MobileLayout data-id="element-1501">
        <div className="text-center py-20" data-id="element-1502">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-1503">
            Appartamento non trovato.
          </p>
          <Link to="/cm/clienti/appartamenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-1504">
            <ArrowLeft size={15} data-id="element-1505" />
            Torna agli appartamenti
          </Link>
        </div>
      </MobileLayout>;
  }
  const remaining = apartment.cleaningsTotal - apartment.cleaningsDone;
  const pct = apartment.cleaningsTotal > 0 ? Math.round(apartment.cleaningsDone / apartment.cleaningsTotal * 100) : 0;
  const completed = apartment.interventions.filter(i => i.status === 'Completata' && i.score !== null);
  const avgScore = completed.length > 0 ? Math.round(completed.reduce((s, i) => s + (i.score ?? 0), 0) / completed.length) : null;
  const totalEarnings = apartment.interventions.reduce((s, i) => s + i.earning, 0);
  return <MobileLayout data-id="element-1506">
      {/* Back */}
      <Link to="/cm/clienti/appartamenti" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-1507">
        <ArrowLeft size={16} data-id="element-1508" />
        Appartamenti
      </Link>

      {/* Header card */}
      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden mb-6" data-id="element-1509">
        <div className="flex flex-col sm:flex-row" data-id="element-1510">
          <img src={apartment.cover} alt={apartment.name} className="w-full sm:w-44 h-40 sm:h-auto object-cover flex-shrink-0 bg-gray-100" data-id="element-1511" />
          <div className="p-4 lg:p-5 flex-1 min-w-0" data-id="element-1512">
            <div className="flex items-start justify-between gap-3" data-id="element-1513">
              <div className="min-w-0" data-id="element-1514">
                <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-1515">
                  <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1516">
                    {apartment.name}
                  </h1>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_STYLE[apartment.type]}`} data-id="element-1517">
                    {apartment.type}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-1518">
                  <MapPin size={14} className="flex-shrink-0" data-id="element-1519" />
                  <span className="truncate" data-id="element-1520">
                    {apartment.address} · {apartment.zone}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5" data-id="element-1521">
                  <User size={13} className="flex-shrink-0" data-id="element-1522" />
                  Referente: {apartment.referente}
                </div>
              </div>
              {apartment.contract?.active ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0" data-id="element-1523">
                  <CheckCircle size={13} data-id="element-1524" />
                  Contratto attivo
                </span> : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200 flex-shrink-0" data-id="element-1525">
                  Nessun contratto
                </span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs className="mb-6" scrollable value={tab} onValueChange={id => setTab(id as DetailTab)} items={[{
      id: 'overview',
      label: 'Overview'
    }, {
      id: 'specifiche',
      label: 'Specifiche casa'
    }, {
      id: 'interventi',
      label: 'Interventi'
    }, {
      id: 'contratto',
      label: 'Contratto e Guadagni'
    }]} data-id="element-1526" />

      {/* OVERVIEW */}
      {tab === 'overview' && <div className="space-y-6" data-id="element-1527">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-1528">
            <StatCard icon={<CheckCircle size={18} data-id="element-1530" />} label="Pulizie eseguite" value={String(apartment.cleaningsDone)} accent="teal-deep" data-id="element-1529" />
            <StatCard icon={<Clock size={18} data-id="element-1532" />} label="Pulizie rimanenti" value={apartment.cleaningsTotal > 0 ? String(remaining) : '—'} accent="coral" data-id="element-1531" />
            <StatCard icon={<Star size={18} data-id="element-1534" />} label="Score medio" value={avgScore !== null ? String(avgScore) : '—'} sub={avgScore !== null ? 'su 100' : undefined} accent="emerald" data-id="element-1533" />
            <StatCard icon={<Wallet size={18} data-id="element-1536" />} label="Guadagno totale" value={`€${totalEarnings}`} accent="navy" data-id="element-1535" />
          </div>

          {/* Cleaning progress */}
          {apartment.cleaningsTotal > 0 && <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1537">
              <div className="flex items-center justify-between mb-2" data-id="element-1538">
                <h2 className="text-sm font-semibold text-navy" data-id="element-1539">
                  Avanzamento contratto
                </h2>
                <span className="text-sm font-semibold text-navy" data-id="element-1540">
                  {apartment.cleaningsDone}/{apartment.cleaningsTotal}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2" data-id="element-1541">
                <div className="bg-coral h-2 rounded-full transition-all duration-300" style={{
            width: `${pct}%`
          }} data-id="element-1542" />
              </div>
              <p className="text-xs text-muted-foreground" data-id="element-1543">
                {remaining} pulizie ancora disponibili nel pacchetto attivo.
              </p>
            </div>}

          {/* Mini score trend */}
          {completed.length > 0 && <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1544">
              <div className="flex items-center gap-2 mb-4" data-id="element-1545">
                <TrendingUp size={16} className="text-teal-deep" data-id="element-1546" />
                <h2 className="text-sm font-semibold text-navy" data-id="element-1547">
                  Qualità nel tempo
                </h2>
              </div>
              <div className="flex items-end gap-2 h-28" data-id="element-1548">
                {[...completed].reverse().slice(-8).map(i => <div key={i.id} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-1549">
                      <div className="w-full bg-gray-100 rounded-md flex items-end h-full" data-id="element-1550">
                        <div className="w-full bg-teal-deep rounded-md transition-all" style={{
                height: `${i.score ?? 0}%`
              }} title={`${i.score}`} data-id="element-1551" />
                      </div>
                      <span className="text-[10px] text-gray-400" data-id="element-1552">
                        {i.score}
                      </span>
                    </div>)}
              </div>
            </div>}
        </div>}

      {/* SPECIFICHE CASA (read-only, host-defined) */}
      {tab === 'specifiche' && (() => {
      const specs = getApartmentSpecs(apartment.id);
      return <div className="space-y-6" data-id="element-1553">
              <div className="flex items-start gap-2 bg-navy/5 border border-navy/10 rounded-xl p-3" data-id="element-1554">
                <Sparkles size={16} className="text-navy flex-shrink-0 mt-0.5" data-id="element-1555" />
                <p className="text-xs text-navy" data-id="element-1556">
                  Specifiche impostate dall'host. Condividile con il cleaner
                  assegnato: vanno sempre rispettate durante l'intervento.
                </p>
              </div>

              {/* Accesso */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1557">
                <div className="flex items-center gap-2 mb-4" data-id="element-1558">
                  <KeyRound size={16} className="text-coral" data-id="element-1559" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-1560">Accesso</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1561">
                  <div className="rounded-lg border border-border p-3" data-id="element-1562">
                    <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-1563">
                      Modalità
                    </span>
                    <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-1564">
                      {specs.access.type}
                    </span>
                  </div>
                  {specs.access.code && <div className="rounded-lg border border-border p-3" data-id="element-1565">
                      <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-1566">
                        Codice
                      </span>
                      <span className="block text-sm font-semibold text-navy mt-0.5 font-mono tracking-wide" data-id="element-1567">
                        {specs.access.code}
                      </span>
                    </div>}
                </div>
                {specs.access.notes && <p className="mt-3 text-sm text-foreground bg-gray-50 border border-gray-100 rounded-lg p-3" data-id="element-1568">
                    {specs.access.notes}
                  </p>}
              </div>

              {/* Preferenze */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1569">
                <div className="flex items-center gap-2 mb-4" data-id="element-1570">
                  <Heart size={16} className="text-coral" data-id="element-1571" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-1572">
                    Preferenze
                  </h2>
                </div>
                {specs.preferences.desiderata.length > 0 && <div className="mb-4" data-id="element-1573">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-1574">
                      Desiderata host
                    </h3>
                    <ul className="space-y-2" data-id="element-1575">
                      {specs.preferences.desiderata.map((d, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm text-foreground" data-id="element-1576">
                          <CheckCircle size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1577" />
                          {d}
                        </li>)}
                    </ul>
                  </div>}
                {specs.preferences.favProducts.length > 0 && <div data-id="element-1578">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-1579">
                      Prodotti preferiti
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-1580">
                      {specs.preferences.favProducts.map((p, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-1581">
                          <Check size={12} data-id="element-1582" />
                          {p}
                        </span>)}
                    </div>
                  </div>}
              </div>

              {/* Punti di attenzione */}
              <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1583">
                <div className="flex items-center gap-2 mb-4" data-id="element-1584">
                  <AlertTriangle size={16} className="text-amber-500" data-id="element-1585" />
                  <h2 className="text-sm font-semibold text-navy" data-id="element-1586">
                    Punti di attenzione
                  </h2>
                </div>
                {specs.attention.delicateMaterials.length > 0 && <div className="mb-4" data-id="element-1587">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-1588">
                      Materiali delicati
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-1589">
                      {specs.attention.delicateMaterials.map((m, idx) => <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-1590">
                          {m}
                        </span>)}
                    </div>
                    {specs.attention.materialNote && <p className="mt-3 text-sm text-amber-800 bg-amber-50/60 border border-amber-100 rounded-lg p-3" data-id="element-1591">
                        {specs.attention.materialNote}
                      </p>}
                  </div>}
                {specs.attention.avoidProducts.length > 0 && <div data-id="element-1592">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2" data-id="element-1593">
                      Prodotti da evitare
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-1594">
                      {specs.attention.avoidProducts.map((p, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200" data-id="element-1595">
                          <Ban size={12} data-id="element-1596" />
                          {p}
                        </span>)}
                    </div>
                  </div>}
              </div>
            </div>;
    })()}

      {/* INTERVENTI */}
      {tab === 'interventi' && <div data-id="element-1597">
          {apartment.interventions.length > 0 ? <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-id="element-1598">
              <div className="overflow-x-auto" data-id="element-1599">
                <table className="w-full text-left border-collapse min-w-[680px]" data-id="element-1600">
                  <thead data-id="element-1601">
                    <tr className="bg-gray-50 border-b border-border" data-id="element-1602">
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1603">
                        Data
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1604">
                        Tipo
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1605">
                        Eseguita da
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1606">
                        Score
                      </th>
                      <th className="px-4 lg:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-1607">
                        Dettaglio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border" data-id="element-1608">
                    {apartment.interventions.map(i => <tr key={i.id} onClick={() => navigate(`/cm/clienti/appartamenti/${apartment.id}/interventi/${i.id}`)} className="hover:bg-gray-50/50 transition-colors cursor-pointer" data-id="element-1609">
                        <td className="px-4 lg:px-6 py-4" data-id="element-1610">
                          <div className="flex items-center gap-2 text-sm text-navy font-medium whitespace-nowrap" data-id="element-1611">
                            <Calendar size={14} className="text-gray-400" data-id="element-1612" />
                            {i.date}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-1613">
                          <span className="text-sm text-gray-700 whitespace-nowrap" data-id="element-1614">
                            {i.type}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-1615">
                          <div className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap" data-id="element-1616">
                            <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-navy" data-id="element-1617">
                              {i.cleaner.split(' ').map(p => p[0]).join('')}
                            </span>
                            {i.cleaner}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4" data-id="element-1618">
                          {i.score !== null ? <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${scoreColor(i.score)}`} data-id="element-1619">
                              <Sparkles size={12} data-id="element-1620" />
                              {i.score}
                            </span> : <span className="text-xs text-gray-400" data-id="element-1621">—</span>}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-right" data-id="element-1622">
                          <span className="inline-flex items-center justify-end text-coral" data-id="element-1623">
                            <ChevronRight size={18} data-id="element-1624" />
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-1625">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-1626">
                <FileText size={20} className="text-muted-foreground" data-id="element-1627" />
              </div>
              <p className="text-sm font-medium text-foreground" data-id="element-1628">
                Nessun intervento eseguito
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-id="element-1629">
                Gli interventi compariranno qui una volta completati.
              </p>
            </div>}
        </div>}

      {/* CONTRATTO E GUADAGNI */}
      {tab === 'contratto' && <div className="space-y-6" data-id="element-1630">
          {/* Contract */}
          <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1631">
            <div className="flex items-center gap-2 mb-4" data-id="element-1632">
              <FileText size={16} className="text-coral" data-id="element-1633" />
              <h2 className="text-sm font-semibold text-navy" data-id="element-1634">
                Contratto attivo
              </h2>
            </div>
            {apartment.contract?.active ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1635">
                <div className="rounded-lg border border-border p-3" data-id="element-1636">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-1637">
                    Piano
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-1638">
                    {apartment.contract.plan}
                  </span>
                </div>
                <div className="rounded-lg border border-border p-3" data-id="element-1639">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-1640">
                    Scadenza
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-1641">
                    {apartment.contract.expiry}
                  </span>
                </div>
                <div className="rounded-lg border border-border p-3" data-id="element-1642">
                  <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground" data-id="element-1643">
                    Prezzo / pulizia
                  </span>
                  <span className="block text-sm font-semibold text-navy mt-0.5" data-id="element-1644">
                    €{apartment.contract.pricePerClean}
                  </span>
                </div>
              </div> : <p className="text-sm text-muted-foreground" data-id="element-1645">
                Nessun contratto attivo per questo appartamento.
              </p>}
          </div>

          {/* Earnings */}
          <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-1646">
            <div className="flex items-center gap-2 mb-4" data-id="element-1647">
              <Wallet size={16} className="text-teal-deep" data-id="element-1648" />
              <h2 className="text-sm font-semibold text-navy" data-id="element-1649">Guadagni</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-1650">
              <div className="rounded-lg bg-navy/5 p-4" data-id="element-1651">
                <div className="text-2xl font-bold text-navy" data-id="element-1652">
                  €{totalEarnings}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1653">
                  Totale generato
                </div>
              </div>
              <div className="rounded-lg bg-coral/5 p-4" data-id="element-1654">
                <div className="text-2xl font-bold text-coral" data-id="element-1655">
                  €
                  {completed.length > 0 ? Math.round(totalEarnings / completed.length) : 0}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1656">
                  Media a intervento
                </div>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1657">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" data-id="element-1658">
                Dettaglio incassi
              </h3>
              {apartment.interventions.map(i => <div key={i.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-1659">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-1660">
                    <div className="w-8 h-8 rounded-full bg-teal-deep/10 flex items-center justify-center flex-shrink-0" data-id="element-1661">
                      <CheckCircle size={14} className="text-teal-deep" data-id="element-1662" />
                    </div>
                    <div className="min-w-0" data-id="element-1663">
                      <div className="text-sm font-medium text-navy truncate" data-id="element-1664">
                        {i.type}
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-1665">
                        {i.date} · {i.cleaner}
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-navy flex-shrink-0" data-id="element-1666">
                    €{i.earning}
                  </span>
                </div>)}
              {apartment.interventions.length === 0 && <p className="text-sm text-muted-foreground" data-id="element-1667">
                  Nessun incasso registrato.
                </p>}
            </div>
          </div>
        </div>}
    </MobileLayout>;
}